import { useState } from 'react';
import { XIcon } from 'lucide-react';
import { NotificationSection, Severity } from '../../types';
import { cn } from '../../utils';
import { Button } from '../Button';
import { NotificationToastIcon } from './NotificationToastIcon';
import { formatTime, formatDate } from './NotificationToast.utils';

export interface CustomToastProps {
  variant?: Severity;
  title?: string;
  description: string;
  taskId?: string;
  onClose?: () => void;
  createdAt?: string;
  seenAt?: string | null;
  isExtension?: boolean;
  fullWidth?: boolean;
  section?: string;
  action?: NotificationToastAction;
}

export interface NotificationToastAction {
  label?: string;
  onClick?: (taskId: string) => void;
  isLoading?: boolean;
}

export const NotificationToast = ({
  variant = 'info',
  title,
  taskId,
  description,
  onClose,
  createdAt,
  seenAt,
  isExtension,
  action,
  section,
  fullWidth = false,
}: CustomToastProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const formatedDate =
    section === NotificationSection.Earlier
      ? formatDate(createdAt)
      : formatTime(createdAt);

  const toggleExpanded = () => {
    setIsExpanded(prev => !prev);
  };

  return (
    <div
      onClick={toggleExpanded}
      className={cn('flex flex-col rounded-lg border bg-white p-4 shadow-lg', {
        'border-primary border-2': seenAt === null,
        'max-w-[462px]': !isExtension,
        'w-full min-[601px]:w-[380px]': isExtension,
        'w-full min-[601px]:w-full': fullWidth,
      })}
    >
      <div className="flex items-start">
        <div className="flex-1">
          <div className="flex gap-1">
            <div className="size-4 flex-shrink-0">
              <NotificationToastIcon severity={variant} />
            </div>
            {title && (
              <p className="text-primary text-xs leading-4 font-semibold">
                {title}
              </p>
            )}
          </div>
          <div
            className={cn(
              'text-muted-foreground mt-2 text-xs leading-4 transition-all duration-200',
              {
                'line-clamp-3': !isExpanded,
              }
            )}
          >
            {description}
          </div>
        </div>

        {createdAt ? (
          <span className="ml-2 text-xs text-gray-500">{formatedDate}</span>
        ) : (
          <button
            type="button"
            className="text-primary ml-auto flex-shrink-0 cursor-pointer items-center"
            onClick={e => {
              e.stopPropagation();
              onClose?.();
            }}
          >
            <XIcon className="size-3" />
          </button>
        )}
      </div>
      {action && isExtension && (
        <div className="mt-4 flex justify-end">
          <Button
            variant="outline"
            size="sm"
            disabled={action.isLoading}
            onClick={() => action.onClick?.(taskId || '')}
          >
            {action.label}
          </Button>
        </div>
      )}
    </div>
  );
};
